/**
 * firstScreen - the ONE function that decides where a signed-in person goes
 * on page load and right after sign-in.
 *
 * v4.39 - 2026-10-31 - BATCH-2026-10-31 row 40 (option A2, Branko approved 2026-10-30): "Skip for now" on Connect
 *                      your data saves a note on the company (/api/me connectSkipped). With the note, a company in
 *                      discovery_complete or connections_ready opens the dashboard (not onboarding, which looped back
 *                      to Connect your data). onboardingStillOpen() says the owner may still open /onboarding to
 *                      connect a source and run Finish Setup. The workflow states are unchanged.
 * v4.29 - 2026-10-13 - ONBOARDING-NOT-ENFORCED-01: before this file App.tsx and
 *                      AuthLanding.tsx carried different rules; the route table
 *                      sent a person with workflowState='basic_info_complete'
 *                      and no captiveComplete to the dashboard while AuthLanding
 *                      sent the same person to onboarding. One rule now lives
 *                      here; every signed-in redirect calls firstScreenFor.
 *
 * The rule is AuthLanding's existing one, deliberately preserved:
 *   captiveComplete === 'in_progress' → '/onboarding'
 *   captiveComplete === 'done'        → '/dashboard'
 *   workflowState is a completed state ('complete' | 'pipeline_complete' | 'setup_complete') → '/dashboard'
 *   anything else, including 'basic_info_complete' and null → '/onboarding'
 *
 * No role rule and no customer text live here; this file decides first screen
 * only. The MemberWaiting page is a separate concern (whoever lands on
 * `/onboarding` and is not an owner sees it); it rides on top of the same
 * firstScreenFor answer.
 */

export type FirstScreen = '/onboarding' | '/dashboard';

export interface FirstScreenInput {
  captiveComplete: string | null | undefined;
  workflowState: string | null | undefined;
  /** v4.39 - row 40: /api/me connectSkipped (the saved "skipped for now" note). Absent = false. */
  connectSkipped?: boolean | null;
}

/** v4.39 - row 40: the states in which Skip for now is offered and the note is honoured. */
const CONNECT_SKIP_STATES = new Set<string>(['discovery_complete', 'connections_ready']);

function hasConnectSkip(input: FirstScreenInput): boolean {
  return input.connectSkipped === true
    && typeof input.workflowState === 'string'
    && CONNECT_SKIP_STATES.has(input.workflowState);
}

/**
 * Completed workflow states (the three names AuthLanding has always honoured).
 * Not exported as a list of literal strings to callers; use `isCompletedWorkflowState`.
 */
const COMPLETED_WORKFLOW_STATES = new Set<string>(['complete', 'pipeline_complete', 'setup_complete']);

export function isCompletedWorkflowState(workflowState: string | null | undefined): boolean {
  return typeof workflowState === 'string' && COMPLETED_WORKFLOW_STATES.has(workflowState);
}

/** True when /api/me already reports the setup is done. Used to gate `?setup=complete`. */
export function isSetupDone(input: FirstScreenInput): boolean {
  if (input.captiveComplete === 'done') return true;
  if (isCompletedWorkflowState(input.workflowState)) return true;
  return false;
}

/** Where a signed-in person's first screen is. One rule, used in every redirect. */
export function firstScreenFor(input: FirstScreenInput): FirstScreen {
  if (input.captiveComplete === 'in_progress') return '/onboarding';
  if (input.captiveComplete === 'done') return '/dashboard';
  if (isCompletedWorkflowState(input.workflowState)) return '/dashboard';
  if (hasConnectSkip(input)) return '/dashboard'; // v4.39 - row 40: skipped for now -> the dashboard, no loop
  return '/onboarding';
}

/**
 * v4.39 - row 40: true when the company skipped Connect your data and setup is not done yet. The owner may then
 * still open /onboarding (Connect your data, the OAuth return and Finish Setup live there) even though the first
 * screen is the dashboard; the dashboard shows the Connect your data banner.
 */
export function onboardingStillOpen(input: FirstScreenInput): boolean {
  return hasConnectSkip(input) && !isSetupDone(input) && input.captiveComplete !== 'in_progress';
}
